/**
 * PuzzleReviewSession — practice queue for unlocked puzzles.
 * Route host: /puzzles/:domain
 */
import React, { useEffect, useState } from 'react';
import PuzzlePlayer from './PuzzlePlayer';

const API_BASE = '/scenario-os';
const TOKEN_KEY = 'scenario_os_token';

function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function PuzzleReviewSession({ learnerId, domain = 'ai', onExit }) {
  const [phase, setPhase] = useState('loading');
  const [queue, setQueue] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [index, setIndex] = useState(0);
  const [solvedCount, setSolvedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(
          `${API_BASE}/puzzles/queue?domain=${encodeURIComponent(domain)}`,
          { headers: authHeaders() },
        );
        if (!res.ok) throw new Error(`Queue fetch failed: ${res.status}`);
        const data = await res.json();
        const items = data.queue || [];
        if (!items.length) {
          if (!cancelled) setPhase('empty');
          return;
        }
        const loaded = [];
        for (const item of items) {
          const sid = item.scene_id;
          if (!sid) continue;
          const r = await fetch(`${API_BASE}/puzzles/${encodeURIComponent(sid)}`, {
            headers: authHeaders(),
          });
          if (!r.ok) continue;
          const scene = await r.json();
          loaded.push({ ...scene, _queueStatus: item.status });
        }
        if (!cancelled) {
          if (!loaded.length) setPhase('empty');
          else {
            setQueue(items);
            setScenes(loaded);
            setPhase('reviewing');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err.message || String(err));
          setPhase('error');
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [learnerId, domain]);

  if (phase === 'loading') {
    return <div style={muted}>Loading practice puzzles…</div>;
  }
  if (phase === 'empty') {
    return (
      <div style={panel}>
        <h3 style={{ marginTop: 0 }}>No practice puzzles yet</h3>
        <p style={muted}>
          Complete a module with an in-lesson puzzle to unlock practice replays here.
        </p>
        {onExit ? <button type="button" style={btn} onClick={onExit}>Back</button> : null}
      </div>
    );
  }
  if (phase === 'error') {
    return <div style={panel}><p style={{ color: '#fecaca' }}>{errorMsg}</p></div>;
  }
  if (phase === 'complete') {
    return (
      <div style={panel}>
        <h3 style={{ marginTop: 0 }}>Nice work</h3>
        <p>You worked through {solvedCount} puzzle{solvedCount === 1 ? '' : 's'} this session.</p>
        {onExit ? <button type="button" style={btn} onClick={onExit}>Done</button> : null}
      </div>
    );
  }

  const scene = scenes[index];
  return (
    <div>
      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 12 }}>
        Puzzle {index + 1} of {scenes.length}
        {queue[index]?.status === 'solved' ? ' · previously solved' : ''}
      </div>
      <PuzzlePlayer
        scene={scene}
        domain={domain}
        onComplete={() => {
          setSolvedCount((c) => c + 1);
          if (index + 1 >= scenes.length) setPhase('complete');
          else setIndex((i) => i + 1);
        }}
      />
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button
          type="button"
          style={btnGhost}
          disabled={index + 1 >= scenes.length}
          onClick={() => {
            if (index + 1 >= scenes.length) setPhase('complete');
            else setIndex((i) => i + 1);
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

const muted = { color: '#94a3b8', fontSize: 14 };
const panel = {
  background: 'rgba(15,23,42,0.9)',
  border: '1px solid rgba(148,163,184,0.25)',
  borderRadius: 12,
  padding: 24,
  color: '#e2e8f0',
};
const btn = {
  marginTop: 12,
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 16px',
  cursor: 'pointer',
};
const btnGhost = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#cbd5e1',
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
};
